import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import showFrequentFoodies from '@firsttable/functions/dist/frequentFoodies';
import { hasPermission } from '@firsttable/functions/dist/user';
import { Avatar, Box, Flex, Text } from '@firsttable/web-components/atoms';
import BodyTitle from '@firsttable/web-components/atoms/BodyTitle/BodyTitle';
import Divider from '@firsttable/web-components/atoms/Divider';
import IconFrequentFoodies from '@firsttable/web-components/atoms/Icon/IconFrequentFoodies';
import IconMoreV from '@firsttable/web-components/atoms/Icon/IconMoreV';
import { themeGet } from '@styled-system/theme-get';
import dynamic from 'next/dynamic';
import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import AnalyticsButton from '@components/analytics/components/Button';
import Link from '@components/analytics/components/Link';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import withMouseDown from '@/components/analytics/hocs/withMouseDown';
import HowItWorksModal from '../HowItWorksModal';
import { ADMIN_URL, CMS_URL } from '../../../lib/env';
import { useAuthState } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import SilverstripeContext from '../../context/Silverstripe';
import NavButtonComponent from '../../GatsbyButtonConvertor';
import NavLinkComponent from '../../GatsbyLinkConvertor';
import { isGiftVouchersEnabledForSite } from '../../../lib/giftVouchers';
import { getProfilePaths } from '../../layouts/ProfileLayout';

const SignUp = dynamic(() => import('../Auth/SignUp'));
const SignIn = dynamic(() => import('../Auth/SignIn'));

// The header corner reserves the wider of the two account states at every breakpoint (the avatar on phones, the signed-out cluster from the tablet breakpoint up) so the swap once auth is read cannot move the header. Measured from the rendered clusters; the corner grows if a cluster ever exceeds this, so a wider cluster shows up as overflow rather than as a shift.
export const ACCOUNT_CORNER_WIDTH = ['44px', '408px', '420px'];

type UserMenuProps = {
  height: {
    mobile: string;
    desktop: string;
  };
  transparent: boolean;
};

const UserMenuStyled = styled(Box)`
  position: relative;
  justify-content: center;
`;
const Dropdown = styled(Box)`
  z-index: 1;
  position: absolute;
  right: 0;
  width: 240px;
  border-radius: 2px;
  padding: 8px 0;
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  a {
    display: block;
    padding: 8px 20px;
    @media (hover: hover) {
      &:hover {
        background: ${themeGet('colors.brand.blue')};
        color: #fff;
        * {
          color: #fff;
        }
      }
    }
    &:focus,
    &:hover {
      text-decoration: none !important;
    }
  }
`;

const EmailText = styled(Text)`
  text-overflow: ellipsis;
`;

// see here: https://usehooks.com/useOnClickOutside/
function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}

const AvatarWithTracking = withMouseDown(Flex, 'avatar user menu', 'dropdown');

const UserMenu = ({ height, transparent }: UserMenuProps) => {
  const { showModal } = useModal();
  const [menuState, setMenuState] = useState(false);
  const { isLoggedIn, user, logout } = useAuthState();
  const ref = useRef();
  useOnClickOutside(ref, () => {
    setMenuState(false);
  });
  const { track } = useAnalytics();
  const toggleDropdown = () => {
    setMenuState((prevMenuState) => !prevMenuState);
  };

  // Grab profilePaths from the ProfileLayout
  const profilePaths = getProfilePaths(user);

  const { page } = useContext(SilverstripeContext);

  let restaurantID = '';
  if (page?.className === 'Restaurant') {
    restaurantID = page?.id;
  }

  if (!isLoggedIn) {
    return (
      <TrackProvider section="header" style={{ display: 'flex' }}>
        <Box
          display={['flex', 'none']}
          alignItems="center"
          justifyContent="center"
          onClick={() => toggleDropdown()}
          role="button"
          aria-label="login"
          ref={ref}
          width={34}
          px={5}>
          <IconMoreV color={transparent ? 'white' : 'brand.blue'} />
          <TrackProvider component_name="dropdown">
            <Dropdown
              display={menuState ? 'block' : 'none'}
              top={['60px', '92px']}
              colors="dropdown">
              <ul>
                <li>
                  <TrackProvider button_type="navigation" button_name="faqs">
                    <Link NavComponent={NavLinkComponent} to="/frequently-asked-questions">
                      FAQs
                    </Link>
                  </TrackProvider>
                </li>
                <li>
                  <TrackProvider button_type="navigation" button_name="our story">
                    <Link NavComponent={NavLinkComponent} to="/our-story">
                      Our Story
                    </Link>
                  </TrackProvider>
                </li>
                <li>
                  <TrackProvider button_type="navigation" button_name="login">
                    <Link
                      onClick={() => {
                        showModal(<SignIn />);
                      }}
                      style={{
                        display: 'block',
                        paddingLeft: 20,
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}>
                      Login
                    </Link>
                  </TrackProvider>
                </li>
                <li>
                  <TrackProvider button_type="navigation" button_name="sign up">
                    <Link
                      color="brand.red"
                      onClick={() => {
                        showModal(<SignUp isModal />, {
                          type: 'signUp',
                        });
                      }}
                      style={{
                        display: 'block',
                        paddingLeft: 20,
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}>
                      Sign Up
                    </Link>
                  </TrackProvider>
                </li>
                <li>
                  <TrackProvider button_type="navigation" button_name="for restaurateurs">
                    <Link NavComponent={NavLinkComponent} to="/restaurants">
                      For Restaurateurs
                    </Link>
                  </TrackProvider>
                </li>
              </ul>
            </Dropdown>
          </TrackProvider>
        </Box>
        <Box display={['none', 'flex']} alignItems="center">
          <TrackProvider link_type="navigation" link_name="how it works">
            <HowItWorksModal transparent={transparent} />
          </TrackProvider>

          <TrackProvider link_type="navigation" link_name="sign up">
            <Link
              mr="m"
              onClick={() => {
                showModal(<SignUp isModal />, { type: 'signUp' });
              }}
              color="brand.red"
              display="flex"
              height="100%"
              data-testid="signUp__button"
              as="a"
              tabIndex="-1">
              <Box alignSelf="center" fontSize={[null, 'xs', 'm']}>
                Sign Up
              </Box>
            </Link>
          </TrackProvider>

          <TrackProvider link_type="navigation" link_name="login">
            <Link
              mr="m"
              onClick={() => {
                showModal(<SignIn />);
              }}
              color={transparent ? 'white' : 'brand.blue'}
              display="flex"
              height="100%"
              data-testid="login__button"
              as="a"
              tabIndex="-1">
              <Box alignSelf="center" fontSize={[null, 'xs', 'm']}>
                Login
              </Box>
            </Link>
          </TrackProvider>

          <TrackProvider button_type="navigation" button_name="for restaurateurs">
            <AnalyticsButton
              NavComponent={NavButtonComponent}
              href="/restaurants"
              kind="outline"
              size="s">
              For Restaurateurs
            </AnalyticsButton>
          </TrackProvider>
        </Box>
      </TrackProvider>
    );
  }
  return (
    <TrackProvider section="header">
      <Box display="flex" alignItems="center">
        <div className="hidden sm:block">
          <TrackProvider button_type="navigation" button_name="how it works">
            <HowItWorksModal transparent={transparent} />
          </TrackProvider>
        </div>
        <UserMenuStyled
          borderLeft="1px solid transparent"
          borderRight="1px solid transparent"
          borderColor={[null, transparent ? null : 'grayscale.300']}
          pr={[0, 3]}
          height={[height.mobile, height.desktop]}
          width={['44px', '95px']}
          data-testid="login__avatar"
          ref={ref}
          onClick={() => toggleDropdown()}>
          <AvatarWithTracking
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
            role="button"
            aria-label="toggle"
            textAlign="center"
            px={5}>
            <Box>
              {user?.avatarURL ? (
                <Box
                  width={['32px', '36px']}
                  height={['32px', '36px']}
                  borderRadius="50%"
                  bg="grayscale.300"
                  backgroundImage={`url(${user.avatarURL
                    .replace(/=36/gim, '=100')
                    .replace(/36x36/, '100x100')})`}
                  backgroundRepeat="no-repeat"
                  backgroundPosition="center"
                  backgroundSize="cover"
                  boxShadow="0 0 0 2px #EBEBEB"
                />
              ) : (
                <Avatar color="brand.red" width={['32px', '36px']} height={['32px', '36px']} />
              )}
            </Box>
          </AvatarWithTracking>
          <TrackProvider component_name="dropdown">
            <Dropdown
              display={menuState ? 'block' : 'none'}
              top={['60px', '92px']}
              colors="dropdown">
              <ul>
                <li>
                  <TrackProvider button_type="navigation" button_name="profile">
                    <Link
                      to={
                        showFrequentFoodies(user) && user?.currentLoyaltyTier
                          ? '/profile/frequent-foodies'
                          : '/profile'
                      }
                      NavComponent={NavLinkComponent}>
                      <Text
                        mb="-5px"
                        fontWeight="bold"
                        // eslint-disable-next-line tailwindcss/no-custom-classname
                        className="amp-mask">
                        {user.firstName} {user.surname}
                      </Text>
                      <EmailText
                        m={0}
                        fontSize="xs"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        // eslint-disable-next-line tailwindcss/no-custom-classname
                        className="amp-mask">
                        {user.email}
                      </EmailText>
                      {showFrequentFoodies(user) && user?.currentLoyaltyTier && (
                        <BodyTitle
                          mt="xs"
                          mb={0}
                          size="xs"
                          color={`loyalty.${user.currentLoyaltyTier.definition.title.toLowerCase()}`}>
                          <IconFrequentFoodies
                            color={`loyalty.${user.currentLoyaltyTier.definition.title.toLowerCase()}`}
                            mr={10}
                            mt={-2}
                            width={16}
                            height={19}
                          />
                          {user.currentLoyaltyTier.definition.title}
                        </BodyTitle>
                      )}
                    </Link>
                  </TrackProvider>
                  <Divider my="xs" />
                </li>
                {hasPermission(['ADMIN', 'RESTAURANT_ADMINISTRATION']) && (
                  <li className="separated-item">
                    <TrackProvider button_type="navigation" button_name="restaurant admin">
                      <Link
                        href={`${ADMIN_URL}manage/${
                          restaurantID ? `reservations/${restaurantID}` : ''
                        }`}>
                        Restaurant Admin
                      </Link>
                    </TrackProvider>
                    <Divider my="xs" />
                  </li>
                )}
                {profilePaths
                  .filter(({ show }) => show)
                  .map(({ label, path }) => (
                    <li key={path}>
                      <TrackProvider button_type="navigation" button_name={label}>
                        <Link NavComponent={NavLinkComponent} href={path}>
                          {label}
                        </Link>
                      </TrackProvider>
                    </li>
                  ))}
                {isGiftVouchersEnabledForSite() && (
                  <li className="separated-item">
                    <Divider my="xs" />
                    <TrackProvider button_type="navigation" button_name="gift vouchers">
                      <Link NavComponent={NavLinkComponent} href="/gift-vouchers">
                        Gift Vouchers
                      </Link>
                    </TrackProvider>
                  </li>
                )}
                {hasPermission(['CMS_ACCESS_LeftAndMain', 'ADMIN']) && (
                  <li>
                    <Divider my="xs" />
                    <TrackProvider button_type="navigation" button_name="cms">
                      <Link
                        href={`${CMS_URL}admin/${
                          restaurantID ? `pages/edit/show/${restaurantID}` : ''
                        }`}>
                        CMS
                      </Link>
                    </TrackProvider>
                  </li>
                )}
                {hasPermission(['CRM_ADMINISTRATION', 'ADMIN']) && (
                  <li>
                    <TrackProvider button_type="navigation" button_name="gmail crm">
                      <Link
                        href={`${CMS_URL}GmailCRM/${
                          restaurantID ? `restaurant/${restaurantID}` : ''
                        }`}>
                        GMail CRM
                      </Link>
                    </TrackProvider>
                  </li>
                )}
                {hasPermission(['SOCIAL_ADMIN', 'ADMIN']) && (
                  <li>
                    <TrackProvider button_type="navigation" button_name="social admin">
                      <Link href={`${ADMIN_URL}social/`}>Social Admin</Link>
                    </TrackProvider>
                  </li>
                )}
                {hasPermission(['SALES_ADMIN', 'ADMIN']) && (
                  <li>
                    <TrackProvider button_type="navigation" button_name="sales admin">
                      <Link href={`${ADMIN_URL}sales/`}>Sales Admin</Link>
                    </TrackProvider>
                  </li>
                )}
                {hasPermission(['REVIEWS_ADMIN', 'ADMIN']) && (
                  <li>
                    <TrackProvider button_type="navigation" button_name="reviews admin">
                      <Link href={`${ADMIN_URL}reviews/`}>Reviews Admin</Link>
                    </TrackProvider>
                  </li>
                )}
                <li>
                  <Divider my="xs" />
                  <TrackProvider button_type="cta" button_name="sign out">
                    <Link // eslint-disable-line
                      onClick={() => {
                        track('account_logout', {});
                        logout();
                      }}
                      data-testid="login__signout"
                      as="a"
                      tabIndex="-1">
                      Sign out
                    </Link>
                  </TrackProvider>
                </li>
              </ul>
            </Dropdown>
          </TrackProvider>
        </UserMenuStyled>
      </Box>
    </TrackProvider>
  );
};

export default UserMenu;
